//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import $ from "jquery";

import {
  IFeedValidationSummaryResponse,
  IInvalidSampleResponse,
  IScheduleValidationRequest,
  IScheduleValidationResponse,
  IUploadFileResponse,
  IValidateCatalogSourceRequest,
  IValidateCatalogSourceResponse,
  IValidationResultsResponse,
  IValidationStatusResponse,
  IValidSampleResponse,
} from '@apis/catalog-validation/catalogValidationModel';
import { CATALOG_INGESTION_TYPES } from '@catalog-ingestion/constants/catalogSelectionConstants';
import { serializedGatewayFetch } from '@utils/sessionFetch';

function catalogIngestionGatewayFetch(
  endpoint: string,
  init?: RequestInit,
  useFetchHeader?: boolean
): Promise<Response> {
  return serializedGatewayFetch('web_gateway', `/api/v1/catalog-ingestion/${endpoint}`, init, useFetchHeader);
}

export async function setEcommmerceType(selectedCatalogType?: CATALOG_INGESTION_TYPES) {
  await catalogIngestionGatewayFetch('set-ecommerce-type', {
    method: 'PUT',
    body: JSON.stringify({
      ecommerceIntegrationType: selectedCatalogType,
    }),
  });
}

export async function validateCatalogSource(
  catalogCredentials: IValidateCatalogSourceRequest
): Promise<IValidateCatalogSourceResponse> {
  let res: Response;
  res = await catalogIngestionGatewayFetch('validate-location', {
    method: 'POST',
    body: JSON.stringify({
      ...catalogCredentials,
    })
  });
  return await res.json();
}

export async function scheduleCatalogValidation(
  scheduleValidationData: IScheduleValidationRequest
): Promise<IScheduleValidationResponse> {
  const res = await catalogIngestionGatewayFetch('validate', {
    method: 'POST',
    body: JSON.stringify({
      ...scheduleValidationData,
    }),
  });
  return await res.json();
}

export async function uploadFile(uploadFileObject: File): Promise<IUploadFileResponse> {
  // create FormData object with the passed file
  const formData = new FormData();
  formData.append(uploadFileObject.name, uploadFileObject);
  // set options
  const options = {
    method: 'POST',
    body: formData,
  };
  // make api request
  try {
    const res = await catalogIngestionGatewayFetch('upload-stream', options, true);
    if (!res.ok) {
      return { s3path: null, name: null };
    } else {
      return await res.json();
    }
  } catch (e) {
    return { s3path: null, name: null };
  }
}

export async function getCatalogValidation(token: string): Promise<IFeedValidationSummaryResponse> {
  const queryParams = $.param({
    token,
  });
  const res = await catalogIngestionGatewayFetch(`getValidation?${queryParams}`);
  return await res.json();
}

export async function getCatalogValidationStatus(token: string): Promise<IValidationStatusResponse> {
  const queryParams = $.param({
    token,
  });
  const res = await catalogIngestionGatewayFetch(`status?${queryParams}`);
  return await res.json();
}

// This returns 100 values at a time so will have to be hit processedRows/100 times rounded up.
export async function getCatalogValidationResults(token: string, page?: number): Promise<IValidationResultsResponse> {
  const queryParams = $.param({
    token,
    page,
  });
  const res = await catalogIngestionGatewayFetch(`results?${queryParams}`);
  return await res.json();
}

export async function getCatalogValidationValidSample(token: string): Promise<IValidSampleResponse> {
  const res = await catalogIngestionGatewayFetch(`${token}/sample/valid`);
  return await res.json();
}

export async function getCatalogValidationInvalidSample(token: string): Promise<IInvalidSampleResponse> {
  const res = await catalogIngestionGatewayFetch(`${token}/sample/invalid`);
  return await res.json();
}

export async function onboardCatalogValidation(token: string) {
  await catalogIngestionGatewayFetch(`${token}/onboard`, {method: 'POST'});
}
